import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { Compass, Search, Heart, BookHeart, Bot, Bell, User, Settings, LogOut, Sparkles, Gift, Crown, Bookmark, LifeBuoy, MessageCircle, Users, ShieldCheck, HelpCircle, Info, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FooterLinks, FOOTER_LINKS } from "@/components/FooterLinks";

const ASSIST_ROLE_LABEL = { wali: "Wali / Parent", imam: "Imam", registrar: "Islamic Marriage Registrar / Officiant", officiant: "Islamic Marriage Registrar / Officiant", parent: "Parent" };

const MAIN = [
  { to: "/discovery", label: "Discover", icon: Compass },
  { to: "/search", label: "Search", icon: Search },
  { to: "/matches", label: "Matches", icon: Heart },
  { to: "/saved", label: "Saved", icon: Bookmark },
  { to: "/marriage-board", label: "Marriage Board", icon: MessageCircle },
  { to: "/stories", label: "Stories", icon: BookHeart },
  { to: "/ai-coach", label: "AI Coach", icon: Bot },
];

const BOTTOM = [
  { to: "/discovery", label: "Discover", icon: Compass },
  { to: "/matches", label: "Matches", icon: Heart },
  { to: "/marriage-board", label: "Board", icon: MessageCircle },
  { to: "/ai-coach", label: "Coach", icon: Bot },
  { to: "/profile", label: "Profile", icon: User },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => { try { const { data } = await api.get("/notifications"); if (active) setUnread(data.unread || 0); } catch {} };
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const initials = (user?.profile?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const doLogout = async () => { await logout(); navigate("/"); };

  if (user?.is_assistant) {
    return (
      <div className="min-h-screen bg-ivory">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-emerald-900/10 bg-ivory/90 px-4 glass sm:px-6">
          <Link to="/assist/browse" data-testid="assist-home-link"><Logo size="md" /></Link>
          <span className="hidden min-w-0 truncate text-sm text-slate-500 md:inline" data-testid="assist-role-label">{ASSIST_ROLE_LABEL[user?.assistant_role] || "Assistant"} · {user?.profile?.name}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/assist/browse")} data-testid="assist-nav-browse" className="rounded-full border-emerald-800/30 text-emerald-900"><Search className="mr-1.5 h-4 w-4" /> Browse</Button>
            <Button variant="outline" onClick={() => navigate("/assisted")} data-testid="assist-nav-linked" className="hidden rounded-full border-emerald-800/30 text-emerald-900 sm:inline-flex"><Users className="mr-1.5 h-4 w-4" /> Linked members</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="assist-avatar-btn"><Avatar className="h-9 w-9 border border-amber-500/40">{user?.profile?.photo && <AvatarImage src={user.profile.photo} alt="" />}<AvatarFallback className="bg-emerald-800 text-sm text-amber-200">{initials}</AvatarFallback></Avatar></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm"><p className="font-semibold text-emerald-900">{user?.profile?.name}</p><p className="truncate text-xs text-slate-500">{user?.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/assist/browse")} data-testid="assist-menu-browse"><Search className="mr-2 h-4 w-4" /> Browse profiles</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/assist/browse")} data-testid="assist-menu-profile"><UserCog className="mr-2 h-4 w-4" /> My profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/assisted")} data-testid="assist-menu-linked"><Users className="mr-2 h-4 w-4" /> Linked members</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="assist-menu-account"><Settings className="mr-2 h-4 w-4" /> Account &amp; security</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/safety")} data-testid="assist-menu-safety"><ShieldCheck className="mr-2 h-4 w-4" /> Safety &amp; scam warnings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/faq")} data-testid="assist-menu-faq"><HelpCircle className="mr-2 h-4 w-4" /> Help &amp; FAQ</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="text-red-600" data-testid="assist-menu-logout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6 sm:pt-8">{children}</main>
      </div>
    );
  }

  const sideLink = ({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-800 text-white" : "text-emerald-900/70 hover:bg-mint hover:text-emerald-900"}`;

  return (
    <div className="min-h-screen bg-ivory">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-emerald-900/10 bg-white px-4 py-5 lg:flex">
        <Link to="/discovery" className="mb-6 px-1" data-testid="nav-home-link"><Logo size="md" /></Link>
        <nav className="flex flex-1 flex-col gap-1">
          {MAIN.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={sideLink} data-testid={`nav-${label.toLowerCase().replace(" ", "-")}-link`}><Icon className="h-5 w-5" /> {label}</NavLink>
          ))}
          <div className="my-3 h-px bg-emerald-900/10" />
          <NavLink to="/premium" className={sideLink} data-testid="nav-premium-link"><Sparkles className="h-5 w-5" /> Premium</NavLink>
          <NavLink to="/family-mode" className={sideLink} data-testid="nav-family-mode-link"><Users className="h-5 w-5" /> Family Mode</NavLink>
          <NavLink to="/assisted" className={sideLink} data-testid="nav-assisted-link"><Users className="h-5 w-5" /> Assisted Matchmaking</NavLink>
          <NavLink to="/invite" className={sideLink} data-testid="nav-invite-link"><Gift className="h-5 w-5" /> Invite</NavLink>
          <NavLink to="/settings" className={sideLink} data-testid="nav-settings-link"><Settings className="h-5 w-5" /> Account &amp; security</NavLink>
        </nav>
        <div className="mt-2 flex items-center gap-3 rounded-xl border border-emerald-900/10 bg-ivory p-2.5">
          <Avatar className="h-9 w-9 border border-amber-500/40">
            {user?.profile?.photo && <AvatarImage src={user.profile.photo} alt="" />}
            <AvatarFallback className="bg-emerald-800 text-xs text-amber-200">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-emerald-900">{user?.profile?.name}</p>
            <p className="flex items-center gap-1 truncate text-xs text-slate-500">{user?.is_premium ? <><Crown className="h-3 w-3 text-amber-500" /> Premium</> : "Free member"}</p>
          </div>
          <button onClick={doLogout} data-testid="sidebar-logout" className="rounded-lg p-2 text-slate-400 hover:bg-mint hover:text-emerald-900" title="Sign out"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>

      {/* Top bar (mobile + desktop content offset) */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-emerald-900/10 bg-ivory/85 px-4 glass sm:px-6">
          <Link to="/discovery" className="lg:hidden"><Logo size="md" /></Link>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            {!user?.is_premium && (
              <button onClick={() => navigate("/premium")} data-testid="nav-premium-btn" className="hidden items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 sm:inline-flex"><Sparkles className="h-4 w-4" /> Go Premium</button>
            )}
            <button onClick={() => navigate("/saved")} data-testid="nav-saved-btn" className="rounded-full p-2 text-emerald-900/70 hover:bg-mint" title="Saved profiles"><Bookmark className="h-5 w-5" /></button>
            <button onClick={() => navigate("/notifications")} data-testid="nav-notifications-btn" className="relative rounded-full p-2 text-emerald-900/70 hover:bg-mint">
              <Bell className="h-5 w-5" />
              {unread > 0 && <span className="absolute -right-0 -top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="notif-badge">{unread}</span>}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-avatar-btn"><Avatar className="h-9 w-9 border border-amber-500/40">{user?.profile?.photo && <AvatarImage src={user.profile.photo} alt="" />}<AvatarFallback className="bg-emerald-800 text-sm text-amber-200">{initials}</AvatarFallback></Avatar></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm"><p className="font-semibold text-emerald-900">{user?.profile?.name}</p><p className="truncate text-xs text-slate-500">{user?.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> My Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/family-mode")} data-testid="menu-family-mode"><Users className="mr-2 h-4 w-4" /> Family Mode (Wali access)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/assisted")} data-testid="menu-assisted"><Users className="mr-2 h-4 w-4" /> Assisted Matchmaking</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/invite")} data-testid="menu-invite"><Gift className="mr-2 h-4 w-4" /> Invite Friends</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/premium")} data-testid="menu-premium"><Sparkles className="mr-2 h-4 w-4" /> Premium</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/safety")} data-testid="menu-safety"><ShieldCheck className="mr-2 h-4 w-4" /> Safety &amp; scam warnings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/faq")} data-testid="menu-faq"><HelpCircle className="mr-2 h-4 w-4" /> Help &amp; FAQ</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/about")} data-testid="menu-about"><Info className="mr-2 h-4 w-4" /> About Nikah Match</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" /> Account &amp; security</DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="menu-help-legal"><LifeBuoy className="mr-2 h-4 w-4" /> Help &amp; Legal</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                    {FOOTER_LINKS.map(([label, href]) => (
                      <DropdownMenuItem key={href} asChild data-testid={`menu-help-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="text-red-600" data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 pb-24 pt-6 sm:px-6 sm:pt-8 lg:pb-10">
          {children}
          <footer className="mt-10 border-t border-emerald-900/10 pt-5 lg:hidden" data-testid="mobile-help-footer">
            <p className="mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400"><LifeBuoy className="h-3.5 w-3.5" /> Help &amp; Legal</p>
            <FooterLinks className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs text-slate-400" linkClassName="hover:text-emerald-700" />
          </footer>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-emerald-900/10 bg-white/95 px-2 pb-safe pt-1.5 glass lg:hidden">
        <div className="mx-auto flex max-w-md items-center justify-around">
          {BOTTOM.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`bottomnav-${label.toLowerCase()}`} className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[11px] font-medium ${isActive ? "text-emerald-800" : "text-slate-400"}`}>
              {({ isActive }) => (<><Icon className={`h-5 w-5 ${isActive ? "text-emerald-700" : ""}`} strokeWidth={isActive ? 2.4 : 2} /> {label}</>)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
